import {
  Box,
  Button,
  FormControl,
} from '@mui/material';
import Alert from 'components/features/alert/Alert';
import AppTextField from 'components/features/input/TextField';
import AppLoading from 'components/features/loading/AppLoading';
import { PAGE_LIST } from 'constant/pages';
import { RegexValidation } from 'constant/regex';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { commonErrorMessage } from 'utils/error-message';
import { healthFaceDb, postAuthLogin } from 'utils/fetch';
import ja from 'utils/ja';

interface ISettingFormInput {
  appId: { value: string; error: boolean };
  appKey: { value: string; error: boolean };
  url: { value: string; error: boolean, errorMessage?: string };
  privateDbId: { value: string; error: boolean };
  privateDbKey: { value: string; error: boolean };
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ISettingProps { }

const SettingPage: React.FC<ISettingProps> = () => {
  const navigate = useNavigate();

  const [appConfig, setAppconfig] = useState<ISettingFormInput>({
    appId: { value: '', error: false },
    appKey: { value: '', error: false },
    url: { value: process.env.REACT_APP_FACE_DB_API ?? '', error: false, errorMessage: '' },
    privateDbId: { value: '', error: false },
    privateDbKey: { value: '', error: false },
  });

  const [token] = useState<{ accessToken: string }>({ accessToken: '' });
  const [loading, setLoading] = useState<boolean>(false);
  const [configError, setConfigError] = useState<boolean>(false);

  const handleChangeText = (event: React.ChangeEvent<HTMLInputElement>) => {
    const key = event.target.id as keyof ISettingFormInput;
    setAppconfig((oldState) => ({
      ...oldState,
      [key]: {
        value: event.target.value.trim(),
        error: !event.target.value.trim(),
      },
    }));
  };

  useEffect(() => {
    localStorage.removeItem('privateDbId');
    localStorage.removeItem('privateDbKey');
  }, []);

  const handleSubmit = async () => {
    const isValid = Object.values(appConfig).every(field => field.value);
    if (isValid) {
      const isValidUrl = await handleValidateUrl(appConfig.url.value);
      if (!isValidUrl) {
        return;
      }
      try {
        setLoading(true);
        const res = await postAuthLogin({
          appId: appConfig.appId.value,
          appKey: appConfig.appKey.value,
          url: appConfig.url.value,
          privateDbId: appConfig.privateDbId.value,
          privateDbKey: appConfig.privateDbKey.value,
        });
        localStorage.removeItem('accessToken');
        localStorage.setItem('accessToken', res.data.token.accessToken);
        localStorage.setItem('privateDbId', appConfig.privateDbId.value);
        localStorage.setItem('privateDbKey', appConfig.privateDbKey.value);
        token.accessToken = res.data.token.accessToken;
        navigate(PAGE_LIST.HOME.pathname());
      } catch (error) {
        setConfigError(true);
      } finally {
        setLoading(false);
      }
    } else {
      setAppconfig({
        appId: {
          value: appConfig.appId.value,
          error: !appConfig.appId.value,
        },
        appKey: {
          value: appConfig.appKey.value,
          error: !appConfig.appKey.value,
        },
        url: {
          value: appConfig.url.value,
          error: !appConfig.url.value,
          errorMessage: commonErrorMessage.MSE00003(ja.setting.url.label),
        },
        privateDbId: {
          value: appConfig.privateDbId.value,
          error: !appConfig.privateDbId.value,
        },
        privateDbKey: {
          value: appConfig.privateDbKey.value,
          error: !appConfig.privateDbKey.value,
        },
      });
    }
  }

  const handleValidateUrl = async (url: string): Promise<boolean> => {
    const isValidFormat = RegexValidation.urlRegex.test(url);
    const defaultErrorMsg = ja.setting.url.errorMessage;

    const updateConfig = (error: boolean, errorMessage = defaultErrorMsg) => {
      setAppconfig(prev => ({
        ...prev,
        url: { value: url, error, errorMessage },
      }));
    };

    if (!isValidFormat) {
      updateConfig(true);
      return false;
    }

    try {
      setLoading(true);

      const status = await healthFaceDb(url);
      const isUp = status === "UP";

      if (!isUp) {
        updateConfig(true);
      } else {
        updateConfig(false, "");
      }

      return isUp;
    } catch {
      updateConfig(true);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: '60vw',
        boxShadow: '0px 2px 4px 2px #00000030',
        borderRadius: '10px',
        padding: '20px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          gap: '20px',
          '& *': { fontSize: '20px !important' },
        }}
      >
        <Box>
          <FormControl variant='standard' fullWidth>
            <AppTextField
              id='url'
              placeholder={ja.setting.url.placeholder}
              value={appConfig.url.value}
              onChange={handleChangeText}
              title={ja.setting.url.label}
              required
              error={appConfig.url.error}
              errorMessage={appConfig.url.errorMessage}
            />
          </FormControl>
          <FormControl variant='standard' fullWidth sx={{ mt: '20px' }}>
            <AppTextField
              id='appId'
              placeholder={ja.setting.app_id.placeholder}
              value={appConfig.appId.value}
              onChange={handleChangeText}
              title={ja.setting.app_id.label}
              required
              error={appConfig.appId.error}
            />
          </FormControl>

          <FormControl variant='standard' fullWidth sx={{ mt: '20px' }}>
            <AppTextField
              id='appKey'
              placeholder={ja.setting.app_key.placeholder}
              value={appConfig.appKey.value}
              onChange={handleChangeText}
              title={ja.setting.app_key.label}
              required
              error={appConfig.appKey.error}
            />
          </FormControl>

          <FormControl variant='standard' fullWidth sx={{ mt: '20px' }}>
            <AppTextField
              id='privateDbId'
              placeholder={ja.setting.db_id.placeholder}
              value={appConfig.privateDbId.value}
              onChange={handleChangeText}
              title={ja.setting.db_id.label}
              required
              error={appConfig.privateDbId.error}
            />
          </FormControl>

          <FormControl variant='standard' fullWidth sx={{ mt: '20px' }}>
            <AppTextField
              id='privateDbKey'
              placeholder={ja.setting.db_key.placeholder}
              value={appConfig.privateDbKey.value}
              onChange={handleChangeText}
              title={ja.setting.db_key.label}
              required
              error={appConfig.privateDbKey.error}
            />
          </FormControl>

          <Box sx={{ textAlign: 'right', mt: '20px' }}>
            <Button
              sx={{
                fontSize: '20px !important',
              }}
              onClick={handleSubmit}
            >
              {ja.setting.next}
            </Button>
          </Box>
        </Box>
      </Box>
      <AppLoading loading={loading} />
      <Alert
        open={configError}
        onClose={() => setConfigError(false)}
        message={ja.message_error.app_config}
        type='error'
      />
    </Box>
  );
};

export default SettingPage;
